import React, { useRef, useState, useEffect } from "react";
import styles from "./style.module.scss";
import { Badge } from "@amzn/awsui-components-react";
import { SideNavigationProps } from "@amzn/awsui-components-react/polaris/side-navigation";
import { useAppSelector } from "app/store";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Team } from "models/schemas";
import { useHeaderTeamNameSelector } from "pages/team_settings/hooks";
import { useUserNameSelector } from "pages/tasks/hooks";
import { useGetApprovalStatQuery } from "pages/reviews/endpoints";
import { ApprovalSearchType } from "pages/reviews/definitions";
import { usePageVisibility } from "hooks";

type NavItem = {
  type: "link" | "divider";
  text?: string;
  href?: string;
  feature?: string;
  teamId?: string;
  visibleToMember?: boolean;
  visibleToGuest?: boolean;
};

/**
 * Hook for handling side navigation events
 */
export const useOnFollowHandler = () => {
  const navigate = useNavigate();

  return (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
    if (!event.detail.external) {
      event.preventDefault();
      navigate(event.detail.href, { replace: true });
    }
  };
};

export const useOrderedNavItems = () => {
  const { t } = useTranslation();
  const teamName = useHeaderTeamNameSelector();
  const allTeams = useAppSelector((state) => state.teamSettings?.allTeams);
  const { userName: currentUser } = useUserNameSelector();
  const sessionExpired = useAppSelector((state) => state.auth.sessionExpired);
  const isTabVisible = usePageVisibility();
  const { currentData: pendingApprovalsData } = useGetApprovalStatQuery(
    {
      teamName,
      searchType: ApprovalSearchType.APPROVER_PENDING_APPROVALS,
      user: currentUser,
    },
    {
      pollingInterval: !isTabVisible ? 0 : 60000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: !teamName || !currentUser || sessionExpired,
    },
  );
  const pendingCount = pendingApprovalsData?.count;
  const showBadge = pendingCount !== undefined && pendingCount > 0;
  const badgeText = showBadge
    ? pendingCount! > 99
      ? "99+"
      : String(pendingCount)
    : "";

  const prevCountRef = useRef<number | undefined>(undefined);
  const [glowing, setGlowing] = useState(false);

  useEffect(() => {
    if (pendingCount !== undefined && prevCountRef.current !== pendingCount) {
      setGlowing(true);
      const t = setTimeout(() => setGlowing(false), 1500);
      prevCountRef.current = pendingCount;
      return () => clearTimeout(t);
    }
    prevCountRef.current = pendingCount;
  }, [pendingCount]);

  const isLoading = !allTeams || !teamName;

  const selectedTeamFeatures = new Set<string>(
    (allTeams && "teams" in allTeams ? (allTeams as any).teams : []).find(
      (team: Team) => team?.teamId === teamName,
    )?.enabledFeatures || [],
  );

  const orderedNavItems: NavItem[] = [
    {
      type: "link",
      text: t("pages.home.title"),
      href: "/home",
      feature: "HOME_PAGE",
      visibleToGuest: true,
    },
    { type: "divider", feature: "HOME_PAGE", visibleToGuest: true },
    {
      type: "link",
      text: t("pages.audits.title"),
      href: "/audits/",
      feature: "AUDIT",
      visibleToGuest: true,
    },
    {
      type: "link",
      text: "Notices",
      href: "/notices/",
      feature: "NOTICE",
      visibleToGuest: true,
    },
    {
      type: "link",
      text: "Litigations",
      href: "/litigations/",
      feature: "LITIGATION",
      visibleToGuest: true,
    },
    { type: "divider", visibleToMember: true, visibleToGuest: true },
    {
      type: "link",
      text: t("pages.tasks.title"),
      href: "/tasks",
      visibleToMember: true,
      visibleToGuest: true,
    },
    {
      type: "link",
      text: t("pages.reviews.title"),
      href: "/reviews",
      visibleToMember: true,
      visibleToGuest: true,
    },
    {
      type: "link",
      text: t("pages.drafts.title"),
      href: "/drafts",
      feature: "FILE_SCAN",
    },
    {
      type: "link",
      text: t("pages.data.title"),
      href: "/data/",
      visibleToMember: true,
    },
    {
      type: "link",
      text: t("pages.dataInsights.title"),
      href: "/data-insights",
      feature: "DATA_INSIGHTS",
    },
    {
      type: "link",
      text: t("pages.agentWork.title"),
      href: "/agent-work",
      feature: "AGENT_WORK",
    },
    {
      type: "link",
      text: t("pages.events.title"),
      href: "/events/",
      feature: "EVENT",
      visibleToGuest: false,
    },
    { type: "divider", visibleToMember: true },
    {
      type: "link",
      text: t("pages.gaia_ai.chatbot.title"),
      href: "/gaia-ai/chatbot",
      feature: "CHATBOT",
    },
    {
      type: "link",
      text: t("pages.team_files.title"),
      href: "/team-files",
      visibleToMember: true,
    },
    {
      type: "link",
      text: t("pages.team_settings.title"),
      href: "/team",
      visibleToMember: true,
    },
    { type: "divider", visibleToMember: true, teamId: "gaia_dev" },
    {
      type: "link",
      text: "Site admin",
      href: "/site-admin",
      visibleToMember: true,
      teamId: "gaia_dev",
    },
  ];

  const isNavItemVisible = (item: NavItem): boolean => {
    if (item.teamId && teamName !== item.teamId) return false;
    if (teamName !== "guest" && item.visibleToMember) return true;
    if (teamName === "guest" && item.visibleToGuest) return true;
    if (item.feature) return selectedTeamFeatures.has(item.feature);
    return false;
  };

  const navigationItems = orderedNavItems
    .filter(isNavItemVisible)
    .map((item): SideNavigationProps.Item => {
      if (item.type === "divider") {
        return { type: "divider" };
      }
      const text =
        item.feature === "CHATBOT"
          ? React.createElement(
              React.Fragment,
              null,
              item.text,
              " ",
              React.createElement(Badge, null, "beta"),
            )
          : item.href === "/reviews" && showBadge
            ? React.createElement(
                React.Fragment,
                null,
                item.text,
                " ",
                React.createElement(
                  "span",
                  { className: glowing ? styles.glow : "" },
                  React.createElement(Badge, { color: "red" }, badgeText),
                ),
              )
            : item.text!;
      return {
        type: "link",
        text: text as any,
        href: item.href!,
      };
    });

  return { items: navigationItems, isLoading };
};

export const useNavConfig = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { items, isLoading } = useOrderedNavItems();

  return {
    header: {
      href: "",
      text: t("left_nav.title"),
    } as SideNavigationProps.Header,
    items: isLoading ? [] : items,
    activeHref: location.pathname,
  };
};
